import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { deviceSeen, fleetReviewQuery, workspaceQuery, type Badge, type HealthCell, type Workspace, type WorkspaceItem, type WorkspaceModule } from '../api/devices'
import { ApiError } from '../api/client'
import { Spinner } from '../ui/Spinner'
import { bridgeHref, openKey } from './bridge'
import { renderScreen } from './screens'
import { SEEN_LIVE_UNDER, SEEN_STALE_OVER, seenAgeLabel } from './seen'
import { assetUrl } from '../ui/assetUrl'

/**
 * Fisa unei masini, deschisa sub randul ei din lista - inventoree/devices/
 * others/actionTabs.php cu _partials/device_workspace_menu.php, si gesturile
 * din device_workspace.js + ExpandRow (general.js): un singur modul desfacut,
 * optiunea apasata isi deschide grupul, Overview la deschidere (sau fila
 * din `#open=`), eticheta "Live / 3h" care bate secunda, fisa care nu creste
 * peste ecran (`--dw-top` masurat, nu ghicit), butonul Review All pe filele
 * care au raspuns pe flota.
 */

export const OVERVIEW_HREF = 'inventoree/deviceOverview/'

/**
 * Adresa de rezerva a unei optiuni din meniul fisei, si a fisei insesi.
 *
 * Clicul e prins in pagina (`preventDefault`), deci adresa asta conteaza numai
 * cand omul deschide legatura ALTFEL: rotita mouse-ului, Ctrl+clic, "deschide
 * in fila noua". Atunci trebuie sa duca unde duce si clicul.
 *
 * Scria adresa din portalul vechi cu `/app` pus in fata
 * (`/app/proactivitee/deviceAlarms/8`), si in v4 nu exista nicio ruta de forma
 * aia: fisa are o singura adresa pe modul, `<modul>/device/{id}`, iar fila se
 * alege din `#open=` - exact ce trimite si `_remap` din portal. Asa ca fiecare
 * deschidere in fila noua cadea in "Not Found", cu atat mai sigur pentru
 * `cmdb/deviceView/`, `overview/agentSettings/` sau `changes/deviceTodo/`, ale
 * caror module nici nu au fisa in v4.
 *
 * Modulul e cel al FISEI (`from`), nu cel scris in adresa optiunii: fila se
 * deschide in fisa unde stai, iar `startingHref` cauta `#open=` prin toate
 * modulele, deci prefixul optiunii nu-i trebuie nimanui.
 */
export function deviceHref(id: number, from: string, href = '', after = ''): string {
  const tab = openKey(href)
  return `/app/${from}/device/${id}${after}${tab !== '' ? `#open=${tab}` : ''}`
}

export function itemHref(item: Pick<WorkspaceItem, 'href' | 'after'>, id: number, from: string): string {
  return deviceHref(id, from, item.href, item.after)
}

/** `#open=devicePrinters` alege fila al carei href contine "/devicePrinters/". Fara ea, prima: Overview. */
export function startingHref(hash: string, modules: WorkspaceModule[]): string {
  const wanted = hash.replace(/^#open=/, '')
  if (wanted && wanted !== hash) {
    for (const m of modules) {
      const hit = m.items.find((it) => it.href.includes(`/${wanted}/`))
      if (hit) return hit.href
    }
  }
  return OVERVIEW_HREF
}

/**
 * Modulul unei file: `prefer` (grupul desfacut) daca o are, altfel primul
 * modul din meniu care o are. Aceeasi cheie poate sta in doua module -
 * System Alarms e si in Opteemum si in Proactivitee.
 */
export function moduleOfIn(modules: WorkspaceModule[], href: string, prefer: string | null): string | null {
  const has = (m: WorkspaceModule) => m.items.some((it) => it.href === href)
  const preferred = modules.find((m) => m.slug === prefer)
  if (preferred && has(preferred)) return preferred.slug
  return modules.find(has)?.slug ?? null
}

/** $menuBadge din macheta: aceeasi cifra, aceeasi culoare, acelasi titlu. */
export function BadgeMark({ badge }: { badge: Badge | null }) {
  if (!badge) return null
  if (badge.kind === 'label') {
    return (
      <>
        {' '}
        <span className="label font-10">{badge.count.toLocaleString('en-US')}</span>
      </>
    )
  }
  if (badge.kind === 'attached') {
    return (
      <>
        {' '}
        <span className="tab-badge tab-badge-blue" title={`${badge.count} attached device${badge.count === 1 ? '' : 's'}`}>
          {badge.count}
        </span>
        {badge.problems > 0 && (
          <>
            {' '}
            <span className="tab-badge tab-badge-amber" title={`${badge.problems} of them ${badge.problems === 1 ? 'is' : 'are'} not working right now`}>
              <i className="fas fa-exclamation" aria-hidden="true"></i>
            </span>
          </>
        )}
      </>
    )
  }
  const apps = badge.kind === 'startup_apps'
  const tone = apps ? (badge.count > 0 ? 'tab-badge-blue' : 'tab-badge-grey') : badge.stopped > 0 ? 'tab-badge-amber' : badge.count > 0 ? 'tab-badge-blue' : 'tab-badge-grey'
  return (
    <>
      {' '}
      <span className={`tab-badge ${tone}`} title={apps ? 'Session autostart applications' : 'Automatic boot services'}>
        {badge.count}
      </span>
    </>
  )
}

/**
 * SI CIFRA SE INTREABA SINGURA, NU NUMAI CRESTE - `device_workspace.js`.
 *
 * Ceasul de mai jos porneste de la varsta primita la deschidere si numara
 * secundele de atunci; pe o fisa tinuta deschisa crestea la nesfarsit peste o
 * statie care raporteaza in fiecare minut. Meniul cere varsta din nou din
 * minut in minut (`Overview::deviceSeen`, `askDeviceSeen`) - la minut, nu la
 * secunda: o cerere pe secunda ar fi trei mii intr-o ora de consola deschisa -
 * si numai cand fila se vede. Iar orice panou care afla ceva nou o poate pune
 * la loc pe loc, cu `markDeviceSeen(age)` (perechea lui `odMarkDeviceSeen`).
 */
const SEEN_EVERY_MS = 60000
const SEEN_EVENT = 'od:device-seen'

/** `window.odMarkDeviceSeen(ageSeconds)`: alt panou a aflat ca masina a raportat acum `age` secunde. */
export function markDeviceSeen(age: number): void {
  if (typeof window === 'undefined' || !Number.isFinite(age)) return
  window.dispatchEvent(new CustomEvent<number>(SEEN_EVENT, { detail: Math.max(0, Math.round(age)) }))
}

function useDeviceSeen(id: number, initial: number | null): { age: number | null; raw: string } {
  const [age, setAge] = useState<number | null>(initial)
  const [raw, setRaw] = useState('')
  useEffect(() => {
    setAge(initial)
    setRaw('')
    const put = (seconds: number) => {
      setAge(seconds)
      setRaw(stampAgo(seconds))
    }
    const ask = () => {
      if (document.hidden) return
      void deviceSeen(id)
        .then((r) => {
          if (r.ok && typeof r.age_seconds === 'number') put(r.age_seconds)
        })
        .catch(() => undefined)
    }
    const onMark = (ev: Event) => put((ev as CustomEvent<number>).detail)
    const t = setInterval(ask, SEEN_EVERY_MS)
    window.addEventListener(SEEN_EVENT, onMark)
    return () => {
      clearInterval(t)
      window.removeEventListener(SEEN_EVENT, onMark)
    }
  }, [id, initial])
  return { age, raw }
}

/** Ora de acum `seconds` secunde, scrisa ca `DataAccesareDM` - `odMarkDeviceSeen`. */
function stampAgo(seconds: number): string {
  const d = new Date(Date.now() - seconds * 1000)
  const pad = (n: number) => (n < 10 ? '0' + n : String(n))
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** Eticheta de langa Overview, care bate secunda (tickDeviceSeen). Aceeasi
    si in celula "Reported" din banda, ca sa arate acelasi numar ca ora de sus
    (cec-master ee7c118ff): o singura sursa a cifrei. */
function Seen({ raw, age, className = 'dwm-seen' }: { raw: string; age: number; className?: string }) {
  /* Se pleaca de la varsta spusa de server si se numara secundele de atunci: asa
     nu conteaza fusul orar al browserului fata de al serverului. */
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const loaded = Date.now()
    const t = setInterval(() => setElapsed(Math.round((Date.now() - loaded) / 1000)), 1000)
    return () => clearInterval(t)
  }, [age])
  const current = age + elapsed
  const stale = current > SEEN_STALE_OVER
  return (
    <span
      className={`${className}${current < SEEN_LIVE_UNDER ? ' is-live' : ''}${stale ? ' is-stale' : ''}`}
      data-seen={raw}
      title={stale ? `Nothing since ${raw} - more than 8 hours ago` : `Last reported ${raw}`}
    >
      {seenAgeLabel(current)}
    </span>
  )
}

function HealthCells({ cells, id, from, active, seen, onOpen }: {
  cells: HealthCell[]
  id: number
  from: string
  active: number | null
  /** Varsta ultimului raport, cand se stie: celula "Reported" ticaie cu ea. */
  seen: { raw: string; age: number } | null
  onOpen(index: number, href: string): void
}) {
  return (
    <ul className="dwm-hs">
      {cells.map((c, i) => {
        const cls = `dwm-hs-cell${c.tone !== '' ? ` is-${c.tone}` : ''}`
        /* Celula "Reported" nu ramane inghetata pe textul de la incarcare: "1m"
           langa un "11s" care ticaie sus era o contradictie. Cand agentul e
           tacut, textul serverului ("silent") ramane. */
        const ticking = c.label === 'Reported' && c.tone !== 'silent' && seen !== null
        const inner = (
          <>
            {ticking ? (
              <Seen raw={seen.raw} age={seen.age} className="dwm-hs-value dwm-seen" />
            ) : (
              <span className="dwm-hs-value">{c.value}</span>
            )}
            <span className="dwm-hs-label">{c.label}</span>
          </>
        )
        return (
          <li key={i}>
            {c.link ? (
              <a
                href={deviceHref(id, from, c.href)}
                className={`action_button ${cls}${active === i ? ' active' : ''}`}
                title={c.title || undefined}
                onClick={(e) => {
                  e.preventDefault()
                  onOpen(i, c.href)
                }}
              >
                {inner}
              </a>
            ) : (
              <span className={cls} title={c.title || undefined}>
                {inner}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Review All: butonul apare numai pe filele cu `data-review`; sertarul se deschide peste pagina. */
function ReviewAll({ item, id, open, onOpen, onClose }: { item: WorkspaceItem | null; id: number; open: boolean; onOpen(): void; onClose(): void }) {
  const on = item?.review != null
  return (
    <>
      <div className={`dwr-bar${on ? ' is-on' : ''}`}>
        <button type="button" className="btn btn-default btn-sm dwr-btn" style={on ? undefined : { display: 'none' }} onClick={onOpen}>
          <i className="fas fa-layer-group"></i> Review All
        </button>
      </div>
      <div className="dwr-veil" hidden={!open} onClick={onClose}></div>
      <aside className="dwr-panel" hidden={!open} aria-label="Review all machines">
        <div className="dwr-panel-head">
          <span>
            <span className="dwr-panel-title">{item?.label ?? 'Review all'}</span>
            <br />
            <span className="dwr-panel-sub">the same screen, across the whole company</span>
          </span>
          <button type="button" className="dwr-panel-close" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="dwr-panel-body">
          {item && item.review != null && open ? (
            <FleetReviewBody id={id} reviewKey={item.review} href={item.href} />
          ) : item && (
            <div className="dwr-loading">
              {bridgeHref(id, item.href) === null ? (
                /* Fara usa catre portalul vechi: pentru modulele taiate ea se
                   intorcea chiar aici. Vezi `bridgeHref`. */
                'This list has not been rebuilt yet.'
              ) : (
                <>
                  This list is still served by the current portal.{' '}
                  <a href={bridgeHref(id, item.href) ?? undefined} target="_blank" rel="noopener">
                    Open it there <i className="fas fa-external-link-alt font-10"></i>
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  )
}

const FR_CSS = `
.fr-head { padding: 0 16px 10px; }
.fr-question { margin: 0; font-size: 14px; color: #3c4652; }
.fr-scope { margin: 4px 0 0; font-size: 12px; color: #8a939c; }
.fr-scope i { margin-right: 4px; }
.fr-stats { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 16px 12px; }
.fr-stat { flex: 0 0 auto; min-width: 130px; padding: 8px 12px; border-radius: 4px; background: #f2f4f7; border-left: 3px solid #b9c2cd; }
.fr-stat-num { display: block; font-size: 22px; font-weight: 600; line-height: 1.1; color: #2f3b46; }
.fr-stat-label { display: block; font-size: 11px; color: #7b8794; margin-top: 2px; }
.fr-stat.is-bad { background: #fdf6f5; border-left-color: #a0362a; }
.fr-stat.is-bad .fr-stat-num { color: #a0362a; }
.fr-stat.is-ok { background: #f1f7f3; border-left-color: #4b9b6e; }
.fr-stat.is-ok .fr-stat-num { color: #3d6b52; }
.fr-stat.is-unknown { background: #fdf3e3; border-left-color: #e0a800; }
.fr-stat.is-unknown .fr-stat-num { color: #8a6410; }
.fr-search { position: relative; padding: 0 16px 10px; }
.fr-search i { position: absolute; left: 26px; top: 9px; font-size: 12px; color: #9aa4b1; }
.fr-search input { padding-left: 28px; }
.fr-table { margin: 0; }
.fr-table th { font-size: 11px; color: #7b8794; text-transform: uppercase; letter-spacing: .03em; }
.fr-table td { font-size: 13px; vertical-align: middle; }
.fr-table .a-right, .fr-table th.a-right { text-align: right; }
.fr-num { font-variant-numeric: tabular-nums; color: #3c4652; }
.fr-num.is-bad { color: #a0362a; font-weight: 700; }
.fr-os { display: block; font-size: 11px; color: #9aa4b1; }
.fr-here { margin-left: 6px; font-size: 10px; font-weight: 700; color: #3b6ea5; text-transform: uppercase; letter-spacing: .04em; }
.fr-row.is-anchor td { background: #eef4fb; }
.fr-tone { font-size: 12px; }
.fr-tone-bad { color: #a0362a; font-weight: 600; }
.fr-tone-ok { color: #3d6b52; }
.fr-tone-unknown { color: #8a6410; }
`

/**
 * Continutul sertarului "Review All" - `_partials/fleet_review.php`: intrebarea,
 * firma si numarul de masini, casutele de sumar, cautarea si tabelul cu
 * masinile (cele rele intai), fiecare rand ducand pe aceeasi fila a masinii.
 */
function FleetReviewBody({ id, reviewKey, href }: { id: number; reviewKey: string; href: string }) {
  const page = useQuery(fleetReviewQuery(id, reviewKey))
  const [search, setSearch] = useState('')
  if (page.error) {
    const text = page.error instanceof ApiError && page.error.status === 400 ? 'You do not have access to this list.' : page.error.message
    return (
      <div className="alert alert-warning" style={{ margin: 16 }}>
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {text}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data
  if (data.failed !== '') {
    return (
      <div className="alert alert-warning" style={{ margin: 16 }}>
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {data.failed}
      </div>
    )
  }
  const rows = data.rows ?? []
  const columns = data.columns ?? []
  const needle = search.trim().toLowerCase()
  const shown = needle === '' ? rows : rows.filter((r) => `${r.name} ${r.os}`.toLowerCase().includes(needle))
  /* Randul duce pe ACEEASI fila a masinii - `#open=` cu cheia filei care a deschis sertarul. */
  const tab = openKey(href)
  return (
    <>
      <style>{FR_CSS}</style>
      <div className="fr-head">
        <p className="fr-question">{data.question}</p>
        <p className="fr-scope">
          <i className="fas fa-building" aria-hidden="true"></i>
          {data.company !== '' ? data.company : 'this company'} &middot; {rows.length} machine{rows.length === 1 ? '' : 's'}
        </p>
      </div>
      {rows.length === 0 ? (
        <div className="alert alert-info" style={{ margin: '0 16px 16px' }}>
          <i className="fas fa-info-circle" aria-hidden="true"></i> No machine in this company answers this question - the
          screen does not apply to any of them.
        </div>
      ) : (
        <>
          <div className="fr-stats">
            {(data.summary ?? []).map((stat, i) => (
              <div className={`fr-stat is-${stat.tone}`} key={i}>
                <span className="fr-stat-num">{stat.value}</span>
                <span className="fr-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
          <div className="fr-search">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input type="text" className="form-control input-sm" id="frSearch" placeholder="Search machines..." autoComplete="off" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <table className="table table-hover fr-table" id="frTable">
            <thead>
              <tr>
                <th className="a-left">Machine</th>
                {columns.map((c) => (
                  <th key={c.key} className={c.align === 'right' ? 'a-right' : 'a-left'}>
                    {c.label}
                  </th>
                ))}
                <th className="a-left">State</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr className={`fr-row${row.id === data.anchor ? ' is-anchor' : ''}`} key={row.id} data-device={row.id}>
                  <td>
                    <a href={`/app/inventoree/device/${row.id}${tab !== '' ? `#open=${tab}` : ''}`}>{row.name !== '' ? row.name : `#${row.id}`}</a>
                    {row.id === data.anchor && <span className="fr-here">you are here</span>}
                    {row.os.trim() !== '' && <span className="fr-os">{row.os}</span>}
                  </td>
                  {columns.map((c) => {
                    const value = row.values[c.key] ?? 0
                    return (
                      <td key={c.key} className={`${c.align === 'right' ? 'a-right' : 'a-left'} fr-num${c.key === 'stopped' && value > 0 ? ' is-bad' : ''}`}>
                        {value}
                      </td>
                    )
                  })}
                  <td>
                    <span className={`fr-tone fr-tone-${row.tone}`}>{row.note}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  )
}

function Failed() {
  return (
    <div className="panel-body">
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle"></i> Error! Something went wrong.
      </div>
    </div>
  )
}

/**
 * `--dw-top`: de unde incepe randul fata de fereastra, plus 12px de rasuflare.
 * Se masoara la aparitie, la desfacerea unui grup, la schimbarea filei si la
 * redimensionare (o data, dupa 120 ms), si se scrie numai daca s-a schimbat.
 */
export function measureTop(row: HTMLElement, last: number | null, win: { innerWidth: number; innerHeight: number }): number | null {
  if (win.innerWidth < 992) return last
  const offset = Math.round(row.getBoundingClientRect().top)
  if (offset < 0 || offset > win.innerHeight * 0.6) return last
  return offset + 12
}

function Menu({ page, from, active, activeModule, activeCell, onOpen, onOpenCell, openModule, onToggleModule }: {
  page: Workspace
  from: string
  active: string
  activeModule: string | null
  activeCell: number | null
  onOpen(href: string, slug?: string): void
  onOpenCell(index: number, href: string): void
  openModule: string | null
  onToggleModule(slug: string): void
}) {
  const id = page.device.id
  const live = useDeviceSeen(id, page.device.lastSeenAge)
  const seenAge = live.age
  const seenRaw = live.raw !== '' ? live.raw : page.device.lastSeen
  return (
    <ul className="action_tabs device-workspace-menu" data-device={id} data-device-name={page.device.name} data-current={page.current}>
      <li className="dwm-overview-item">
        <a
          href={deviceHref(id, from)}
          className={`action_button dwm-overview${activeCell === null && active === OVERVIEW_HREF ? ' active' : ''}`}
          onClick={(e) => {
            e.preventDefault()
            onOpen(OVERVIEW_HREF)
          }}
        >
          <i className="fas fa-th-large"></i>
          <span className="dwm-overview-label">Overview</span>
          {seenRaw !== '' && seenAge !== null && <Seen raw={seenRaw} age={seenAge} />}
        </a>
      </li>

      {page.health && (
        <li className={`dwm-health${page.health.silent ? ' is-silent' : ''}`}>
          <HealthCells
            cells={page.health.cells}
            id={id}
            from={from}
            active={activeCell}
            seen={seenRaw !== '' && seenAge !== null ? { raw: seenRaw, age: seenAge } : null}
            onOpen={onOpenCell}
          />
        </li>
      )}

      {page.modules.map((m) => (
        <li key={m.slug} className={`dwm-module${openModule === m.slug ? ' is-open' : ''}`} data-module={m.slug}>
          <a
            href="#"
            className="dwm-module-head"
            onClick={(e) => {
              e.preventDefault()
              onToggleModule(m.slug)
            }}
          >
            <span className="dwm-module-icon" style={{ backgroundColor: m.color }}>
              <img src={assetUrl(`images/header/${m.icon}`)} alt="" width={14} height={14} />
            </span>
            <span className="dwm-module-name">{m.name}</span>
            <i className="fas fa-chevron-right dwm-caret" aria-hidden="true"></i>
          </a>

          <ul className="dwm-items">
            {m.items.map((it) => (
              <li key={it.href + it.after}>
                <a
                  href={itemHref(it, id, from)}
                  className={`action_button${activeCell === null && active === it.href && activeModule === m.slug ? ' active' : ''}`}
                  data-review={it.review ?? undefined}
                  data-review-href={it.review ? it.href : undefined}
                  onClick={(e) => {
                    e.preventDefault()
                    onOpen(it.href, m.slug)
                  }}
                >
                  {it.label}
                  <BadgeMark badge={it.badge} />
                </a>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

export function DeviceWorkspace({ id, from }: { id: number; from: string }) {
  const query = useQuery(workspaceQuery(id, from))
  if (query.isPending) return <Spinner />
  if (query.isError || !query.data) return <Failed />
  return <Loaded page={query.data} from={from} />
}

function Loaded({ page, from }: { page: Workspace; from: string }) {
  const id = page.device.id
  const [active, setActive] = useState(() => startingHref(typeof window === 'undefined' ? '' : window.location.hash, page.modules))
  /* Legatura apasata e cea care se coloreaza (inventoree.js: removeClass('active')
     pe toate, addClass pe `this`). O celula din banda se aprinde deci ea, nu
     optiunea din meniu cu aceeasi adresa. */
  const [activeCell, setActiveCell] = useState<number | null>(null)
  const [reload, setReload] = useState(0)
  const [openModule, setOpenModule] = useState<string | null>(() => page.modules.find((m) => m.open)?.slug ?? null)
  /* Modulul optiunii aprinse. Cheia singura nu ajunge: System Alarms sta si
     in Opteemum si in Proactivitee, cu aceeasi cheie (proactivitee/deviceAlarms/),
     si fisa lua primul modul care o avea - un clic in Proactivitee desfacea
     Opteemum. device_workspace.js lua grupul legaturii apasate
     (`closest('.dwm-module')`); aici se tine minte care a fost. */
  const [activeModule, setActiveModule] = useState<string | null>(() => moduleOfIn(page.modules, active, page.modules.find((m) => m.open)?.slug ?? null))
  const [review, setReview] = useState(false)
  const row = useRef<HTMLDivElement>(null)
  const lastTop = useRef<number | null>(null)

  /* `slug` vine din meniu: modulul in care s-a apasat. Un ecran care deschide
     alta fila (`open`, legaturile `more`) nu stie modulul, deci ramane in
     grupul desfacut daca fila e acolo, si abia altfel merge la primul care o are. */
  const open = (href: string, slug?: string) => {
    const module = slug ?? moduleOfIn(page.modules, href, openModule)
    setActive(href)
    setActiveModule(module)
    setActiveCell(null)
    setReload((n) => n + 1)
    /* Grupul optiunii apasate se desface singur - si numai el. */
    if (module) setOpenModule(module)
  }

  /* Celulele din banda de sanatate nu stau in niciun grup (device_workspace.js
     cauta .dwm-module deasupra si nu gaseste), deci ele lasa meniul cum era. */
  const openCell = (index: number, href: string) => {
    setActive(href)
    setActiveCell(index)
    setReload((n) => n + 1)
  }

  const toggleModule = (slug: string) => setOpenModule((cur) => (cur === slug ? null : slug))

  /* `odOpenDeviceSheet` (device_workspace.js) cauta optiunea in meniul fisei si
     spune daca a gasit-o. Aceeasi cautare, pe bucata de adresa, ca in portal. */
  const hasTab = (href: string) => page.modules.some((m) => m.items.some((it) => it.href.includes(href)))

  /* Inaltimea fisei: masurata, nu ghicita. */
  const measure = useCallback(() => {
    const el = row.current
    if (!el) return
    const next = measureTop(el, lastTop.current, window)
    if (next === null || next === lastTop.current) return
    lastTop.current = next
    el.style.setProperty('--dw-top', `${next}px`)
  }, [])

  useLayoutEffect(measure, [measure, openModule, active, reload])
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const onResize = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        lastTop.current = null
        measure()
      }, 120)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (timer) clearTimeout(timer)
    }
  }, [measure])

  const sameHref =
    page.modules.find((m) => m.slug === activeModule)?.items.find((it) => it.href === active) ??
    page.modules.flatMap((m) => m.items).find((it) => it.href === active) ??
    null
  /* Review All se uita la data-review de pe legatura apasata; celulele n-au. */
  const activeItem = activeCell === null ? sameHref : null
  const label = active === OVERVIEW_HREF ? 'Overview' : (sameHref?.label ?? active)
  const content: ReactNode = <div key={`${active}:${reload}`}>{renderScreen(active, label, { id, from, overview: page.overview, open, hasTab })}</div>

  return (
    <div className="row d-flex m-0 dw-row" ref={row}>
      <div className="col-auto bg-lightest-gray dw-menu">
        <Menu page={page} from={from} active={active} activeModule={activeModule} activeCell={activeCell} onOpen={open} onOpenCell={openCell} openModule={openModule} onToggleModule={toggleModule} />
      </div>
      <div className="col-md-12 dw-content">
        <ReviewAll item={activeItem} id={id} open={review} onOpen={() => setReview(true)} onClose={() => setReview(false)} />
        <div className="details_content">{content}</div>
      </div>
    </div>
  )
}
